import { updateNodeField, createChildNode, deleteNode } from './api.js';
import { highlightSearchTerm } from './highlightUtils.js';
import { showMyRolesActive, setSkipNextPanelUpdate, chartInstance, lastPurposeValue, setLastPurposeValue, roleMap } from './state.js';
import { renderRoleReferences } from '../shared/roleReferences.js';
import { loadRoleClaimStatus, loadLeafClaimStatus, toggleRoleClaim, toggleLeafClaim, updateClaimButton, updateLeafClaimButton } from './panel/claims.js';
import { loadRoleUsers } from './panel/roleUsers.js';
import { flattenRoles, findNodeById, populateRoleMap } from './panel/utils.js';
import { buildAddChildForm, buildPlaceholderMessage, buildSuccessMessage, buildRoleDetailsView, buildRoleDetailsEdit } from './panel/domBuilders.js';
import { refreshVisualization } from './refreshVisualization.js';
import { apiGet } from './utils/apiClient.js';
import { showErrorModal } from './utils/modalUtil.js';
import { loadAndDisplayBacklinks as loadBacklinks } from './panel/backlinks.js';
import { attachRoleReferenceHandlers as attachRoleRefHandlers } from './panel/roleReferences.js';
import { initEditModeAutocomplete, cleanupEditModeAutocomplete } from './panel/autocompleteManager.js';
import { findD3NodeById } from './utils/hierarchyUtils.js';
import { t } from './utils/i18n.js';
import type { CirclePackNode } from '../shared/types.js';

// Re-export for tests
export { initEditModeAutocomplete, cleanupEditModeAutocomplete };

type AppConfig = { appName: string; appTagline: string };

// App config cache
let appConfig: AppConfig | null = null;

async function getAppConfig(): Promise<AppConfig> {
    if (!appConfig) {
        try {
            appConfig = await apiGet<AppConfig>('/api/config');
        } catch (error) {
            // Silently fail and use defaults (fetch fails in tests)
        }
    }
    return appConfig || { appName: 'bobble.io', appTagline: 'Clarity in Circles' };
}

/**
 * Remove the edit icon from the panel header
 */
function removeEditIcon(): void {
    const panelHeader = document.querySelector('.role-panel__header');
    if (panelHeader) {
        const editLink = panelHeader.querySelector('#edit-mode-link');
        if (editLink) {
            editLink.remove();
        }
    }
}

/**
 * Wrapper for attachRoleReferenceHandlers that provides required callbacks
 */
export function attachRoleReferenceHandlers(): void {
    attachRoleRefHandlers(displayNodeData, updateSelectedNodeTitle);
}

/**
 * Wrapper for loadAndDisplayBacklinks that provides the attachRoleReferenceHandlers callback
 */
export async function loadAndDisplayBacklinks(roleId: number): Promise<void> {
    return loadBacklinks(roleId, attachRoleReferenceHandlers);
}

/**
 * Show form to add a child role
 */
export function showAddChildForm(nodeName: string): void {
    const panelContent = document.querySelector('.role-panel__content');
    if (!panelContent) return;

    panelContent.innerHTML = '';
    panelContent.appendChild(buildAddChildForm());

    document.getElementById('create-btn')?.addEventListener('click', async () => {
        const childName = (document.getElementById('child-name') as HTMLInputElement).value;
        const childValue = parseInt((document.getElementById('child-value') as HTMLSelectElement).value, 10) || 100;
        const childColor = (document.getElementById('child-color') as HTMLInputElement).value || '#ffffff';

        if (!childName.trim()) {
            await showErrorModal(t('errors.invalidInput'), t('errors.pleaseEnterName'));
            return;
        }

        const createBtn = document.getElementById('create-btn') as HTMLButtonElement;
        createBtn.disabled = true;
        createBtn.textContent = t('panel.create.creating');

        const success = await createChildNode(nodeName, childName, childValue, childColor);

        if (success) {
            panelContent.innerHTML = '';
            panelContent.appendChild(buildSuccessMessage(t('panel.successMessage')));
            // Refresh the chart to show the new role
            await refreshVisualization();
            setTimeout(() => {
                panelContent.innerHTML = '';
                panelContent.appendChild(buildPlaceholderMessage());
            }, 1500);
        } else {
            createBtn.disabled = false;
            createBtn.textContent = t('panel.create.createButton');
            await showErrorModal(t('errors.creationFailed'), t('errors.createChildFailed'));
        }
    });

    document.getElementById('cancel-btn')?.addEventListener('click', () => {
        displayNodeData(null);
    });
}


export let displayNodeData = (node?: CirclePackNode | null, editMode: boolean = false): void => {
    const panelContent = document.querySelector('.role-panel__content') as HTMLElement;

    if (!node || !panelContent) {
        removeEditIcon();
        if (panelContent) {
            panelContent.innerHTML = '';
            panelContent.appendChild(buildPlaceholderMessage());
            delete panelContent.dataset.currentNode;
            // Update title asynchronously
            getAppConfig().then(config => {
                document.title = `${config.appName} – ${config.appTagline}`;
            });
        }
        return;
    }

    if (editMode) {
        displayNodeDataEditable(node);
        return;
    }

    const name = node.name || 'Unnamed';

    // Update page title with selected role (async)
    getAppConfig().then(config => {
        document.title = `${name} – ${config.appName}`;
    });
    
    const nodeId = node.id;
    panelContent.innerHTML = '';
    panelContent.appendChild(buildRoleDetailsView(node, roleMap));

    // Attach event handlers for action buttons
    const addChildIconBtn = document.getElementById('add-child-icon-btn');
    addChildIconBtn?.addEventListener('click', () => {
        showAddChildForm(name);
    });

    const deleteIconBtn = document.getElementById('delete-node-icon-btn') as HTMLButtonElement | null;
    deleteIconBtn?.addEventListener('click', async () => {
        const childCount = node.children?.length || 0;
        const childNames = childCount > 0 && node.children ? node.children.map(c => c.name) : [];
        const success = await deleteNode(nodeId, name, childCount > 0, childNames);
        if (success) {
            displayNodeData(null);
            // Refresh the chart to remove the deleted role
            await refreshVisualization();
        }
    });

    // Add back button handler
    const backBtn = panelContent.querySelector('.role-panel__back-to-my-roles-btn');
    if (backBtn) {
        backBtn.addEventListener('click', () => {
            if (chartInstance && typeof chartInstance.zoomReset === 'function') {
                setSkipNextPanelUpdate(true);
                chartInstance.zoomReset();
            }

            if (typeof (window as any).showMyRolesOverview === 'function') {
                (window as any).showMyRolesOverview();
            }
            
            // Reset the flag after a short delay to ensure normal clicks work again
            setTimeout(() => {
                setSkipNextPanelUpdate(false);
            }, 100);
        });
    }

    const editLink = document.getElementById('edit-mode-link');
    if (editLink) {
        editLink.addEventListener('click', (e) => {
            e.preventDefault();
            displayNodeDataEditable(node);
        });
    }

    // Always set current node (both name and ID) and load users (claim operations handle their own refresh)
    panelContent.dataset.currentNode = node.name;
    panelContent.dataset.nodeId = String(node.id);
    
    // Setup tab switching
    setupPanelTabs(panelContent as HTMLElement);
    
    if (node.id !== undefined) {
        loadRoleUsers(node.id);
    }
    
    // Attach click handlers for role references
    attachRoleReferenceHandlers();
    
    // Load and display backlinks
    if (node.id !== undefined) {
        loadAndDisplayBacklinks(node.id);
    }

    // Attach contained role click handlers (descendants)
    attachContainedRoleHandlers(panelContent);
};

export function displayNodeDataEditable(node): void {
    const panelContent = document.querySelector('.role-panel__content');
    if (!panelContent) return;

    const name = node.name || 'Unnamed';
    const purpose = node.purpose || '';
    const accountabilities = node.accountabilities || [];
    const responsibilities = node.responsibilities || [];
    const children = node.children ? node.children.length : 0;
    const nodeId = node.id;

    panelContent.innerHTML = '';
    panelContent.appendChild(buildRoleDetailsEdit(node));

    (panelContent as HTMLElement).dataset.nodeName = name;
    (panelContent as HTMLElement).dataset.nodeId = nodeId;
    (panelContent as HTMLElement).dataset.accountabilities = JSON.stringify(accountabilities);
    (panelContent as HTMLElement).dataset.responsibilities = JSON.stringify(responsibilities);

    // Store initial purpose value to prevent unnecessary saves
    setLastPurposeValue(purpose);

    // Add view mode link handler
    const viewLink = document.getElementById('view-mode-link');
    if (viewLink) {
        viewLink.addEventListener('click', async (e) => {
            e.preventDefault();
            // Refresh visualization to get all updates
            await refreshVisualization();
            
            // Fetch fresh data for panel display
            try {
                const rolesData = await apiGet<CirclePackNode | CirclePackNode[]>('/api/roles');
                const freshData = Array.isArray(rolesData) ? rolesData[0] : rolesData;
                
                // Update roleMap with fresh data
                const dataArray = Array.isArray(rolesData) ? rolesData : [rolesData];
                populateRoleMap(dataArray);
                
                const updatedNode = findNodeById(freshData, nodeId);
                if (updatedNode) {
                    displayNodeData(updatedNode, false);
                } else {
                    console.warn('Role not found in fresh data, using stale data');
                    displayNodeData(node, false);
                }
            } catch (error) {
                console.error('Error fetching updated role data:', error);
                displayNodeData(node, false);
            }
        });
    }

    // Old button handlers have been moved to buildRoleDetailsView action icons above

    const valueSelect = document.getElementById('edit-value') as HTMLSelectElement | null;
    if (valueSelect) {
        valueSelect.addEventListener('change', async () => {
            const nodeName = valueSelect.dataset.node;
            const field = valueSelect.dataset.field;
            if (!nodeName || !field) return;
            
            const newValue = parseInt(valueSelect.value, 10);

            valueSelect.disabled = true;
            const success = await updateNodeField(nodeName, field, newValue);
            valueSelect.disabled = false;

            if (success) {
                valueSelect.classList.add('saved');
                setTimeout(() => valueSelect.classList.remove('saved'), 500);
            }
        });
    }

    panelContent.querySelectorAll('input[type="text"]:not(.list-item-input)').forEach(input => {
        if ((input as HTMLInputElement).id.startsWith('new-')) return;

        input.addEventListener('keypress', async (e) => {
            if ((e as KeyboardEvent).key === 'Enter') {
                const nodeName = (input as HTMLInputElement).dataset.node;
                const field = (input as HTMLInputElement).dataset.field;
                if (!nodeName || !field) return;
                
                const newValue = (input as HTMLInputElement).value;

                (input as HTMLInputElement).disabled = true;
                const success = await updateNodeField(nodeName, field, newValue);
                (input as HTMLInputElement).disabled = false;

                if (success) {
                    input.classList.add('saved');
                    setTimeout(() => input.classList.remove('saved'), 500);

                    if (field === 'name') {
                        panelContent.querySelectorAll('input[type="text"]').forEach(inp => {
                            (inp as HTMLInputElement).dataset.node = newValue;
                        });
                        (panelContent as HTMLElement).dataset.nodeName = newValue;
                        
                        // Refresh chart to update visualization with new name
                        await refreshVisualization();
                    }
                }
            }
        });
    });

    setupListHandlers(panelContent as HTMLElement);
    
    // Populate initial list items
    rerenderList(panelContent as HTMLElement, 'accountabilities', accountabilities);
    rerenderList(panelContent as HTMLElement, 'responsibilities', responsibilities);
    
    // Attach click handlers for role references
    attachRoleReferenceHandlers();
    
    // Load and display backlinks
    if (nodeId) {
            loadAndDisplayBacklinks(nodeId);
    }
    
    // Initialize autocomplete for all textareas (wait for DOM to be ready)
    setTimeout(() => {
        initEditModeAutocomplete();
    }, 0);
}

async function focusContainedRole(roleId: number): Promise<void> {
    if (!chartInstance || typeof chartInstance.zoomToNode !== 'function' || typeof chartInstance.data !== 'function') {
        return;
    }

    const dataRoot = chartInstance.data();
    const hierarchyRoot = (dataRoot as any)?.__dataNode;
    if (!hierarchyRoot) return;

    const d3Node = findD3NodeById(hierarchyRoot, roleId);
    if (!d3Node) return;

    chartInstance.zoomToNode(d3Node.data, { __dataNode: d3Node });

    displayNodeData(d3Node.data, false);
}

/**
 * Setup tab switching for panel tabs
 */
function setupPanelTabs(panelContent: HTMLElement): void {
    const tabButtons = panelContent.querySelectorAll('.role-panel__tab-btn');
    const tabPanes = panelContent.querySelectorAll('.role-panel__tab');

    tabButtons.forEach(btn => {
        btn.addEventListener('click', () => {
            const tabName = (btn as HTMLElement).dataset.tab;
            if (!tabName) return;

            // Remove active class from all buttons and tabs
            tabButtons.forEach(b => b.classList.remove('role-panel__tab-btn--active'));
            tabPanes.forEach(pane => pane.classList.remove('role-panel__tab--active'));

            // Add active class to clicked button and corresponding tab
            (btn as HTMLElement).classList.add('role-panel__tab-btn--active');
            const activeTab = panelContent.querySelector(`.role-panel__tab[data-tab="${tabName}"]`);
            if (activeTab) {
                activeTab.classList.add('role-panel__tab--active');
            }
            
            // Load backlinks when Referenced tab is activated
            if (tabName === 'referenced') {
                const nodeId = parseInt(panelContent.dataset.nodeId || '', 10);
                if (nodeId) {
                    loadAndDisplayBacklinks(nodeId);
                }
            }
        });
    });
}

function attachContainedRoleHandlers(panelContent: HTMLElement): void {
    const items = panelContent.querySelectorAll('.contained-roles-section .role-reference.role-link');
    items.forEach(item => {
        item.addEventListener('click', async (e) => {
            e.preventDefault();
            const roleId = parseInt((item as HTMLElement).dataset.roleId || '', 10);
            if (!roleId) return;
            await focusContainedRole(roleId);
        });
    });
}

function setupListHandlers(panelContent: HTMLElement): void {
    const nodeName = panelContent.dataset.nodeName;
    if (!nodeName) return;

    panelContent.querySelectorAll('.move-up-btn').forEach(btn => {
        (btn as HTMLButtonElement).onclick = async () => {
            const type = (btn as HTMLButtonElement).dataset.type;
            const indexStr = (btn as HTMLButtonElement).dataset.index;
            if (!type || !indexStr) return;
            
            const index = parseInt(indexStr, 10);
            const currentList = JSON.parse(panelContent.dataset[type] || '[]');

            if (index > 0) {
                [currentList[index - 1], currentList[index]] = [currentList[index], currentList[index - 1]];

                (btn as HTMLButtonElement).disabled = true;
                const success = await updateNodeField(nodeName, type, currentList);

                if (success) {
                    panelContent.dataset[type] = JSON.stringify(currentList);
                    rerenderList(panelContent, type, currentList);
                } else {
                    (btn as HTMLButtonElement).disabled = false;
                }
            }
        };
    });

    panelContent.querySelectorAll('.move-down-btn').forEach(btn => {
        (btn as HTMLButtonElement).onclick = async () => {
            const type = (btn as HTMLButtonElement).dataset.type;
            const indexStr = (btn as HTMLButtonElement).dataset.index;
            if (!type || !indexStr) return;
            
            const index = parseInt(indexStr, 10);
            const currentList = JSON.parse(panelContent.dataset[type] || '[]');

            if (index < currentList.length - 1) {
                [currentList[index], currentList[index + 1]] = [currentList[index + 1], currentList[index]];

                (btn as HTMLButtonElement).disabled = true;
                const success = await updateNodeField(nodeName, type, currentList);

                if (success) {
                    panelContent.dataset[type] = JSON.stringify(currentList);
                    rerenderList(panelContent, type, currentList);
                } else {
                    (btn as HTMLButtonElement).disabled = false;
                }
            }
        };
    });

    panelContent.querySelectorAll('.add-item-btn').forEach(btn => {
        (btn as HTMLButtonElement).onclick = async () => {
            const type = (btn as HTMLButtonElement).dataset.type;
            if (!type) return;
            
            const inputId = type === 'accountabilities' ? 'new-accountability' : 'new-responsibility';
            const textarea = document.getElementById(inputId) as HTMLTextAreaElement | null;
            if (!textarea) return;
            
            const newItem = textarea.value.trim();

            if (!newItem) return;

            const currentList = JSON.parse(panelContent.dataset[type] || '[]');
            currentList.push(newItem);

            (btn as HTMLButtonElement).disabled = true;
            const success = await updateNodeField(nodeName, type, currentList);
            (btn as HTMLButtonElement).disabled = false;

            if (success) {
                panelContent.dataset[type] = JSON.stringify(currentList);
                textarea.value = '';
                rerenderList(panelContent, type, currentList);
            }
        };
    });

    panelContent.querySelectorAll('.remove-btn').forEach(btn => {
        (btn as HTMLButtonElement).onclick = async () => {
            const type = (btn as HTMLButtonElement).dataset.type;
            const indexStr = (btn as HTMLButtonElement).dataset.index;
            if (!type || !indexStr) return;
            
            const index = parseInt(indexStr, 10);
            const currentList = JSON.parse(panelContent.dataset[type] || '[]');

            currentList.splice(index, 1);

            (btn as HTMLButtonElement).disabled = true;
            const success = await updateNodeField(nodeName, type, currentList);

            if (success) {
                panelContent.dataset[type] = JSON.stringify(currentList);
                rerenderList(panelContent, type, currentList);
            } else {
                (btn as HTMLButtonElement).disabled = false;
            }
        };
    });
}

function rerenderList(panelContent: HTMLElement, type: string, currentList: string[]): void {
    const listContainer = document.getElementById(`${type}-list`);
    if (!listContainer) return;
    
    listContainer.innerHTML = '';
    
    currentList.forEach((item, idx) => {
        const listItemDiv = document.createElement('div');
        listItemDiv.className = 'list-item';
        
        const textarea = document.createElement('textarea');
        textarea.className = 'list-item-input';
        textarea.dataset.index = String(idx);
        textarea.dataset.type = type;
        textarea.rows = 2;
        textarea.textContent = item;
        textarea.setAttribute('aria-label', `${type} item ${idx + 1}`);
        
        const moveUpBtn = document.createElement('button');
        moveUpBtn.className = 'move-up-btn';
        moveUpBtn.dataset.index = String(idx);
        moveUpBtn.dataset.type = type;
        moveUpBtn.textContent = '↑';
        moveUpBtn.setAttribute('aria-label', `Move ${type} item ${idx + 1} up`);
        if (idx === 0) {
            moveUpBtn.disabled = true;
            moveUpBtn.setAttribute('aria-disabled', 'true');
        }
        
        const moveDownBtn = document.createElement('button');
        moveDownBtn.className = 'move-down-btn';
        moveDownBtn.dataset.index = String(idx);
        moveDownBtn.dataset.type = type;
        moveDownBtn.textContent = '↓';
        moveDownBtn.setAttribute('aria-label', `Move ${type} item ${idx + 1} down`);
        if (idx === currentList.length - 1) {
            moveDownBtn.disabled = true;
            moveDownBtn.setAttribute('aria-disabled', 'true');
        }
        
        const removeBtn = document.createElement('button');
        removeBtn.className = 'remove-btn';
        removeBtn.dataset.index = String(idx);
        removeBtn.dataset.type = type;
        removeBtn.textContent = '×';
        removeBtn.setAttribute('aria-label', `Remove ${type} item ${idx + 1}`);
        
        listItemDiv.appendChild(textarea);
        listItemDiv.appendChild(moveUpBtn);
        listItemDiv.appendChild(moveDownBtn);
        listItemDiv.appendChild(removeBtn);
        
        listContainer.appendChild(listItemDiv);
    });

    setupListHandlers(panelContent);
    
    // Reinitialize autocomplete for newly created textareas
    cleanupEditModeAutocomplete();
    initEditModeAutocomplete();
}

export function updateSelectedNodeTitle(node): void {
    const titleElement = document.getElementById('selected-node-title');
    if (titleElement) {
        if (node && node.name) {
            titleElement.textContent = node.name;
            titleElement.classList.remove('hidden');
        } else {
            titleElement.textContent = '';
            titleElement.classList.add('hidden');
        }
    }
}

// Expose globally for labels.ts
(window as any).loadLeafClaimStatus = loadLeafClaimStatus;

// Expose toggleLeafClaim globally for double-click handling in labels.ts
(window as any).toggleLeafClaim = toggleLeafClaim;

// Expose loadRoleUsers so it can be passed as callback to claim functions
(window as any).loadRoleUsers = loadRoleUsers;

/**
 * Attach click handlers to role reference spans
 */

// Set up event delegation for blur events on editable fields
// This ensures blur handlers work even after the panel is re-rendered
document.addEventListener('blur', async (e) => {
    const target = e.target as HTMLElement;
    const panelContent = document.querySelector('.role-panel__content') as HTMLElement;
    
    // Check if the blur event is from an element inside the panel
    if (!panelContent || !panelContent.contains(target)) return;
    
    // Handle purpose textarea blur
    if (target.id === 'edit-purpose' && target instanceof HTMLTextAreaElement) {
        const nodeName = target.dataset.node;
        const field = target.dataset.field;
        if (!nodeName || !field) return;
        
        const newValue = target.value;
        
        if (lastPurposeValue !== newValue) {
            setLastPurposeValue(newValue);
            target.disabled = true;
            const success = await updateNodeField(nodeName, field, newValue);
            target.disabled = false;
            
            if (success) {
                target.classList.add('saved');
                setTimeout(() => target.classList.remove('saved'), 500);
            }
        }
    }
    
    // Handle list item textareas (accountabilities/responsibilities)
    if (target instanceof HTMLTextAreaElement && target.classList.contains('list-item-input')) {
        const nodeName = panelContent.dataset.nodeName;
        const type = target.dataset.type;
        const indexStr = target.dataset.index;
        if (!nodeName || !type || !indexStr) return;
        
        const index = parseInt(indexStr, 10);
        const newValue = target.value.trim();
        
        const currentList = JSON.parse(panelContent.dataset[type] || '[]');
        
        if (!newValue) {
            target.value = currentList[index];
            return;
        }
        
        if (currentList[index] !== newValue) {
            currentList[index] = newValue;
            
            target.disabled = true;
            const success = await updateNodeField(nodeName, type, currentList);
            target.disabled = false;
            
            if (success) {
                panelContent.dataset[type] = JSON.stringify(currentList);
                target.classList.add('saved');
                setTimeout(() => target.classList.remove('saved'), 500);
            }
        }
    }
    
    // Handle regular input fields (name, etc.)
    if (target instanceof HTMLInputElement && 
        target.type === 'text' && 
        !target.classList.contains('list-item-input') &&
        !target.id.startsWith('new-') &&
        target.dataset.node &&
        target.dataset.field) {
        
        const nodeName = target.dataset.node;
        const field = target.dataset.field;
        const newValue = target.value;
        
        target.disabled = true;
        const success = await updateNodeField(nodeName, field, newValue);
        target.disabled = false;
        
        if (success) {
            target.classList.add('saved');
            setTimeout(() => target.classList.remove('saved'), 500);
            
            if (field === 'name') {
                panelContent.querySelectorAll('input[type="text"]').forEach(inp => {
                    (inp as HTMLInputElement).dataset.node = newValue;
                });
                panelContent.dataset.nodeName = newValue;
                
                // Refresh chart to update visualization with new name
                await refreshVisualization();
            }
        }
    }
}, true); // Use capture phase to ensure we catch the event

// Set up event delegation for keypress events (Enter to blur)
document.addEventListener('keypress', (e) => {
    if (e.key !== 'Enter') return;
    
    const target = e.target as HTMLElement;
    const panelContent = document.querySelector('.role-panel__content');
    
    if (!panelContent || !panelContent.contains(target)) return;
    
    // Handle Enter key on list item textareas
    if (target instanceof HTMLTextAreaElement && target.classList.contains('list-item-input')) {
        e.preventDefault();
        target.blur();
    }
}, true);
